import { parseMathText, speakMathSegment } from '@unlimitedtests/core'
import { unescapeDisplayText } from '@/lib/render/display-escapes'

/**
 * Renders question text with caret-exponent notation turned into real
 * superscripts: "2^x" → 2ˣ, "3·2^(kx)" → 3·2ᵏˣ, "x^-1" → x⁻¹ (issue #711).
 * Presentation-only — the stored stem/choice/explanation is unchanged.
 * Drop-in for `{stem}`: <MathText>{stem}</MathText> or <MathText text={stem} />.
 */
export default function MathText({
  children,
  text,
  className,
}: {
  children?: string
  text?: string
  className?: string
}) {
  // Leaked source escapes ("\$85", a literal "\n") are never meant to be
  // seen — see lib/render/display-escapes.ts.
  const raw = unescapeDisplayText(text ?? children ?? '')
  const segments = parseMathText(raw)
  return (
    <span className={className}>
      {segments.map((s, i) =>
        s.sup ? (
          // role="img" + aria-label, and NOT an sr-only text twin: a superscript
          // maps to role=superscript, where a name is prohibited and may simply
          // be ignored, while any added text node — display:none included —
          // shifts the persisted highlight offsets that are computed from
          // textContent. This adds no text, so textContent is byte-identical.
          <sup key={i} role="img" aria-label={speakMathSegment(s.text)}>
            {s.text}
          </sup>
        ) : (
          <span key={i}>{s.text}</span>
        ),
      )}
    </span>
  )
}
